import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Channel } from '../data/mockData';
import { toast } from 'sonner';

interface CreateChannelDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (channel: Omit<Channel, 'id' | 'stationId'>) => void;
  existingChannelNumbers: number[];
}

export function CreateChannelDrawer({
  isOpen,
  onClose,
  onCreate,
  existingChannelNumbers,
}: CreateChannelDrawerProps) {
  const [channelNumber, setChannelNumber] = useState('');
  const [channelName, setChannelName] = useState('');
  const [status, setStatus] = useState<'Active' | 'Suspended'>('Active');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const nextChannelNumber = (existingChannelNumbers.length > 0
    ? Math.max(...existingChannelNumbers) + 1
    : 1
  );

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!channelName.trim()) {
      newErrors.channelName = 'Channel name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    if (!validate()) {
      return;
    }

    onCreate({
      channelNumber: nextChannelNumber,
      name: channelName.trim(),
      status,
    });

    toast.success('Channel created successfully');
    handleClose();
  };

  const handleClose = () => {
    setChannelName('');
    setStatus('Active');
    setErrors({});
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-gray-900 border-gray-800 text-white overflow-y-auto p-0"
      >
        <SheetHeader className="px-6 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 border-b border-gray-800">
          <SheetTitle className="text-2xl font-bold text-white">Create Channel</SheetTitle>
        </SheetHeader>

        <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-6">
          {/* Channel Name */}
          <div className="space-y-3 max-w-md">
            <Label htmlFor="channelName" className="text-sm font-semibold text-white">
              Channel Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="channelName"
              type="text"
              placeholder="e.g., Premium Content"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              maxLength={64}
              className={`!bg-[#1f2a3c] dark:!bg-[#1f2a3c] border-gray-700 text-white placeholder:text-gray-500 h-11 focus-visible:!bg-[#243247] dark:focus-visible:!bg-[#243247] ${
                errors.channelName ? 'border-red-500' : ''
              }`}
            />
            {errors.channelName && (
              <p className="text-xs text-red-400 mt-1">{errors.channelName}</p>
            )}
            <p className="text-xs text-gray-500">{channelName.length}/64 characters</p>
          </div>

          {/* Status */}
          <div className="space-y-3 max-w-md">
            <Label htmlFor="status" className="text-sm font-semibold text-white">
              Status
            </Label>
            <Select value={status} onValueChange={(v) => setStatus(v as any)}>
              <SelectTrigger
                id="status"
                className="bg-gray-800 border-gray-700 text-white h-11"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800">
                <SelectItem value="Active" className="text-white focus:bg-gray-800">
                  Active
                </SelectItem>
                <SelectItem value="Suspended" className="text-white focus:bg-gray-800">
                  Suspended
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-6 sm:px-8 py-4 sm:py-5">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-gray-700 text-black hover:text-white hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Create
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}